import * as echarts from 'echarts'

import {
  determineAmPm,
  ECHART_OPTION_GRID_DEFAULTS,
  ECHART_OPTION_X_AXIS_DEFAULTS,
  ECHART_OPTION_Y_AXIS_DEFAULTS,
  LABEL_FONT_SIZE,
  NORMAL_LABEL_COLOR
} from './time-series-chart-util';

(function (Drupal, once) {
  'use strict'

  /**
   * The on/off chart is implemented as a line graph where y is either -1 or 1, where the area between the y value and
   * y = 0 is shaded. -1 represents the "off" state and 1 represents the "on" state.
   *
   * To simplify the drawing process, the line itself is not drawn, leaving only the area below (or above) it shaded.
   *
   * The shading is achieved using EChart's visual map feature.
   *
   * @type {{attach: Drupal.behaviors.timeSeriesOnOffChart.attach}}
   */
  Drupal.behaviors.timeSeriesOnOffChart = {
    attach: function (context) {
      once('chart-initialized', '.time-series-on-off-chart', context).forEach(chartElement => {
        // Parse data points
        const dataAttr = chartElement.dataset.chartData
        if (!dataAttr) return

        let dataPoints = []
        try {
          dataPoints = JSON.parse(dataAttr)
        } catch (e) {
          return
        }

        if (dataPoints.length === 0) return

        // ECharts wants each data point in the format [timeInMilliseconds, value], value being either 1 or -1.
        // Moreover, we need to add in an extra data point when the value changes because otherwise, the vertical edges
        // of the rectangles will be curved. Let's say at 1:00 the value is false and at 1:10 the value is true. At this
        // point, the blue rectangle should end with a straight edge and the red rectangle should start with a straight
        // edge with those two edges forming a straight vertical "line" perpendicular to the x-axis. However, what will
        // happen is that the edges will be curved, forming a curved "line" starting at 1:00 at the bottom of the blue
        // rectangle and ending at 1:10 on the red rectangle.  To fix this, we need to insert an additional point at
        // 1:10 with the value -1.
        const xWindow = (chartElement.dataset.chartXWindow || 'last15h').toLowerCase()

        const chartData = []
        let previousValue
        for (const point of dataPoints) {
          const timeInMs = new Date(point.timestamp).getTime()
          // If the value has changed, insert an additional data point for the previous value with the same timestamp as
          // the current value. ECharts handles this just fine even though there is essentially a timestamp with two
          // different values.
          if (previousValue !== undefined && previousValue !== point.value) {
            chartData.push([timeInMs, previousValue === '1' ? 1 : -1])
            chartData.push([timeInMs, point.value === '1' ? 1 : -1])
          } else {
            // Otherwise add a single data point with a value of either 1 or -1.
            chartData.push([timeInMs, point.value === '1' ? 1 : -1])
          }
          previousValue = point.value
        }

        let minTime
        let maxTime
        if (xWindow === 'full') {
          const times = chartData.map(row => row[0])
          minTime = Math.min(...times)
          maxTime = Math.max(...times)
        } else {
          const mostRecentTimeStamp = chartData[chartData.length - 1][0]
          minTime = mostRecentTimeStamp - (15 * 60 * 60 * 1000)
          maxTime = mostRecentTimeStamp
        }

        // Get custom CSS properties
        const redColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--color-red').trim() || '#ef4444'

        // Get CSS custom property for blue color
        const blueColor = getComputedStyle(document.documentElement)
          .getPropertyValue('--color-navy').trim() || '#ef4444'

        // Initialize chart
        const myChart = echarts.init(chartElement)

        const option = {
          animation: false,
          grid: ECHART_OPTION_GRID_DEFAULTS,
          xAxis: {
            ...ECHART_OPTION_X_AXIS_DEFAULTS,
            min: minTime,
            max: maxTime
          },
          yAxis: {
            ...ECHART_OPTION_Y_AXIS_DEFAULTS,
            min: -2,
            max: 2,
            axisLabel: {
              hideOverlap: true,
              fontSize: LABEL_FONT_SIZE,
              color: NORMAL_LABEL_COLOR,
              formatter: function (value) {
                // Tried all kinds of things to get the labels placed correctly, and all I
                // got was this hack. Hopefully will find a better way later.
                if (value === 2) {
                  return '\n\nOn'
                } else if (value === -2) {
                  return 'Off\n\n'
                }

                return ''
              }
            }
          },
          visualMap: {
            show: false,
            type: 'piecewise',
            pieces: [
              {min: -1, max: 0, color: blueColor},
              {min: 0, max: 1, color: redColor}
            ]
          },
          series: [
            {
              type: 'line',
              data: chartData,
              showSymbol: false, // No dots at data points
              lineStyle: {
                width: 1
              },
              areaStyle: {
                opacity: 1
              }
            }
          ],
          tooltip: {
            trigger: 'axis',
            formatter: function (params) {
              if (params.length === 0) return ''

              const date = new Date(params[0].value[0])
              const value = params[0].value[1]
              const dateStr = echarts.format.formatTime('MM/dd/yyyy', date)
              const ampm = determineAmPm(date)

              return `${dateStr} ${ampm}<br/>Value: ${value === 1 ? 1 : 0}`
            }
          }
        }

        myChart.setOption(option)
        requestAnimationFrame(() => {
          myChart.resize()
        })

        // Handle resize
        let resizeTimeout
        window.addEventListener('resize', function () {
          clearTimeout(resizeTimeout)
          resizeTimeout = setTimeout(function () {
            myChart.resize()
          }, 100)
        })
      })
    }
  }
})(Drupal, once)
